import './ActiveFilterChips.css'

import { cap } from '../../../../State'
import { translations } from '../../../../translations'
import { STATE_ENUM } from '../Functions'

const language = cap.language.state || 'IT'
const t = translations[language] || translations.IT

/**
 * Mostra il contatore dei risultati e, se presenti filtri attivi diversi dai default,
 * le chip rimovibili per ciascun filtro. Stile coerente con la pagina Statistiche.
 * @param {Object} props - Proprietà del componente
 * @param {string} props.selectedDate - Data selezionata (formato DD/MM/YYYY)
 * @param {Function} props.onDateClear - Callback per resettare la data a oggi
 * @param {string} props.selectedIsola - ID isola selezionata
 * @param {Function} props.onIsolaClear - Callback per rimuovere il filtro isola
 * @param {string} props.defaultIsola - Isola di default dell'utente
 * @param {Array} props.isole - Lista delle isole disponibili [{name, id}]
 * @param {Object} props.filters - Valori filtri {traveller, aic, state, searchQuery}
 * @param {Function} props.onFilterClear - Callback per rimuovere un singolo filtro (filterName)
 * @param {Function} props.onResetAll - Callback per resettare tutti i filtri
 * @param {number} props.filteredCount - Numero di risultati filtrati
 * @param {number} props.totalCount - Numero totale di conversazioni
 * @returns {JSX.Element} Componente contatore e chip filtri
 */
export function ActiveFilterChips({
  selectedDate,
  onDateClear,
  selectedIsola,
  onIsolaClear,
  defaultIsola,
  isole,
  filters,
  onFilterClear,
  onResetAll,
  filteredCount,
  totalCount
}) {
  /**
   * Verifica se la data selezionata è diversa dalla data odierna
   * @returns {boolean} true se la data non è oggi
   */
  const isDateDifferentFromToday = () => {
    if (!selectedDate) return false
    const today = new Date()
    const todayStr = `${today.getDate().toString().padStart(2, '0')}/${(today.getMonth() + 1).toString().padStart(2, '0')}/${today.getFullYear()}`
    return selectedDate !== todayStr
  }

  const hasIsolaFilter = selectedIsola && selectedIsola !== defaultIsola
  const hasStateFilter = filters.state && filters.state !== ''
  const hasSearchFilter = filters.searchQuery && filters.searchQuery.trim() !== ''
  const hasDateFilter = isDateDifferentFromToday()
  const hasAnyChip = hasIsolaFilter || hasStateFilter || hasSearchFilter || hasDateFilter

  /**
   * Recupera il nome leggibile dell'isola dal suo ID
   * @returns {string} Nome dell'isola o ID se non trovato
   */
  const getIsolaName = () => {
    const found = isole.find((i) => i.id === selectedIsola)
    return found ? found.name : selectedIsola
  }

  /**
   * Recupera la label leggibile dello stato dal codice
   * @returns {string} Label testuale dello stato
   */
  const getStateLabel = () => {
    if (filters.state === 'PAUSED') return t.inAttesaDiConferma
    if (filters.state === 'TRASFERTA') return t.trasfertaCreata
    const found = STATE_ENUM.find((s) => s.code === filters.state)
    return found ? found.label : filters.state
  }

  return (
    <div className="gigi-results-bar">
      {/* Contatore risultati */}
      <div className="gigi-results-counter">
        <span className="gigi-results-number">{filteredCount}</span>
        <span className="gigi-results-label">
          {filteredCount !== totalCount ? ` / ${totalCount}` : ''}{' '}
          {filteredCount === 1 ? t.conversationSingular : t.conversationPlural}
        </span>
      </div>

      {/* Chip filtri attivi */}
      {hasAnyChip && (
        <div className="gigi-results-chips">
          {hasDateFilter && (
            <button
              type="button"
              className="gigi-result-chip"
              onClick={onDateClear}
              aria-label={`${t.removeFilterDate}: ${selectedDate}`}
              tabIndex={0}
            >
              <i className="pi pi-calendar gigi-result-chip-icon" />
              <span>{selectedDate}</span>
              <i className="pi pi-times gigi-result-chip-close" />
            </button>
          )}

          {hasIsolaFilter && (
            <button
              type="button"
              className="gigi-result-chip"
              onClick={onIsolaClear}
              aria-label={`${t.removeFilterIsland}: ${getIsolaName()}`}
              tabIndex={0}
            >
              <i className="pi pi-map-marker gigi-result-chip-icon" />
              <span>{getIsolaName()}</span>
              <i className="pi pi-times gigi-result-chip-close" />
            </button>
          )}

          {hasStateFilter && (
            <button
              type="button"
              className="gigi-result-chip"
              onClick={() => onFilterClear('state')}
              aria-label={`${t.removeFilterState}: ${getStateLabel()}`}
              tabIndex={0}
            >
              <i className="pi pi-flag gigi-result-chip-icon" />
              <span>{getStateLabel()}</span>
              <i className="pi pi-times gigi-result-chip-close" />
            </button>
          )}

          {hasSearchFilter && (
            <button
              type="button"
              className="gigi-result-chip"
              onClick={() => onFilterClear('searchQuery')}
              aria-label={`${t.removeSearch}: ${filters.searchQuery}`}
              tabIndex={0}
            >
              <i className="pi pi-search gigi-result-chip-icon" />
              <span>"{filters.searchQuery}"</span>
              <i className="pi pi-times gigi-result-chip-close" />
            </button>
          )}
        </div>
      )}
    </div>
  )
}
